import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Skeleton, CircularProgress } from "@mui/material";
import {
  generateCardData,
  DASHBOARD,
} from "../../constants/dashboardConstants";
import DashboardCard from "./DashboardCard";
import DashboardUpcomingTrip from "./DashboardUpcomingTrip.jsx";
import config from "../../config.js";
import "./Dashboard.css";
import { getAlertCounts } from "../../pages/alerts/AlertsApis.js";
import { useTranslation } from "react-i18next";
import { useUser } from "../../pages/login/UserContextPersistent";
import { useSettings } from "../../context/SettingsContext"; // Added import
export default function DashboardDatas() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { state: userState } = useUser();
  const { fetchSettings, settings, loading: settingsLoading } = useSettings(); // Added useSettings
  const currentLanguage = i18n.language;

  // State Management
  const [overview, setOverview] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState({
    vehicles: [],
    routes: [],
    stops: [],
    drivers: [],
    sites: [],
  });
  const [alerts, setAlerts] = useState({
    userAlerts: [],
    vehicleAlerts: [],
    expiryAlerts: [],
  });

  // Key-based permission map
  const [cardKeyMap, setCardKeyMap] = useState({});

  // Define permission map
  const cardPermissionMap = {
    vehicles: { path: "/api/vehicle", action: "GET" },
    routes: { path: "/api/route", action: "GET" },
    stops: { path: "/api/stop", action: "GET" },
    alerts: { path: "/api/alert", action: "GET" },
    drivers: { path: "/api/driver", action: "GET" },
    sites: { path: "/api/site", action: "GET" },
  };

  /**
   * Checks if the user has the required permission
   * @param {string} requiredPath - API path to check
   * @param {string} requiredAction - Action type (GET, POST, etc.)
   * @returns {boolean} - Whether the user has permission
   */
  const hasPermission = (requiredPath, requiredAction = null) => {
    if (!userState?.features || !requiredPath) {
      return true;
    }
    return userState.features.some((feature) => {
      const pathMatches = feature.api_path.includes(requiredPath);
      return requiredAction
        ? pathMatches && feature.action === requiredAction
        : pathMatches;
    });
  };

  /**
   * Stores the original card IDs and their permission types before translation
   */
  const storeOriginalCardKeys = () => {
    const englishCardData = generateCardData(overview, (key) => key);
    const newCardKeyMap = {};
    englishCardData.forEach((card) => {
      for (const [permType, _] of Object.entries(cardPermissionMap)) {
        if (card.title.toLowerCase().includes(permType.toLowerCase())) {
          newCardKeyMap[card.id] = permType;
          break;
        }
      }
    });
    setCardKeyMap(newCardKeyMap);
  };

  /**
   * Filters cards based on user permissions using card IDs instead of titles
   * @param {Array} cardsData - Array of card data objects
   * @returns {Array} - Filtered array of cards
   */
  const filterCardsByPermission = (cardsData) => {
    if (!cardsData || !Array.isArray(cardsData)) return [];
    return cardsData.filter((card) => {
      const permissionType = cardKeyMap[card.id];
      if (!permissionType) return true;
      const permission = cardPermissionMap[permissionType];
      if (!permission) return true;
      return hasPermission(permission.path, permission.action);
    });
  };

  // API Calls
  /**
   * Fetches all vehicles for the organization
   * @returns {Array} Array of vehicle objects
   */
  const getAllVehicles = async () => {
    try {
      const resp = await axios.get(`${config.baseURL}/vehicle/organization`, {
        withCredentials: true,
      });
      return resp.data;
    } catch (error) {
      handleApiError(error);
      return [];
    }
  };

  /**
   * Fetches all routes for the organization
   * @returns {Array} Array of route objects
   */
  const getAllRoutes = async () => {
    try {
      const resp = await axios.get(`${config.baseURL}/route/organization`, {
        withCredentials: true,
      });
      return resp.data;
    } catch (error) {
      handleApiError(error);
      return [];
    }
  };

  /**
   * Fetches all stops for the organization
   * @returns {Array} Array of stop objects
   */
  const getAllStops = async () => {
    try {
      const resp = await axios.get(`${config.baseURL}/stop/organization`, {
        withCredentials: true,
      });
      return resp.data;
    } catch (error) {
      handleApiError(error);
      return [];
    }
  };

  /**
   * Fetches all alerts for the organization
   * @returns {Object} Object containing all types of alerts
   */
  // const getAllAlerts = async () => {
  //   try {
  //     const [userAlerts, vehicleAlerts, expiryAlerts] = await Promise.all([
  //       getUserAlerts(),
  //       getVehicleAlerts(),
  //       getExpiryAlerts(),
  //     ]);
  //     return {
  //       userAlerts,
  //       vehicleAlerts,
  //       expiryAlerts,
  //     };
  //   } catch (error) {
  //     handleApiError(error);
  //     return {
  //       userAlerts: [],
  //       vehicleAlerts: [],
  //       expiryAlerts: [],
  //     };
  //   }
  // };
  const getAllAlerts = async () => {
    try {
      const alertCounts = await getAlertCounts();
      // Transform the response to match the expected format
      return {
        userAlerts: { length: alertCounts.byType.USER || 0 },
        vehicleAlerts: { length: alertCounts.byType.VEHICLE || 0 },
        expiryAlerts: { length: alertCounts.byType.EXPIRY || 0 },
        totalAlerts: alertCounts.total || 0,
      };
    } catch (error) {
      handleApiError(error);
      return {
        userAlerts: { length: 0 },
        vehicleAlerts: { length: 0 },
        expiryAlerts: { length: 0 },
        totalAlerts: 0,
      };
    }
  };

  /**
   * Fetches all drivers for the organization
   * @returns {Array} Array of driver objects
   */
  const getAllDrivers = async () => {
    try {
      const organizationId = userState?.user?.organizationId;
      const resp = await axios.get(
        `${config.baseURL}/driver/organization?organizationId=${organizationId}`,
        {
          withCredentials: true,
        }
      );

      // console.log("Drivers API Response:", resp.data);
      // console.log("Drivers count:", resp.data?.length);

      return resp.data;
    } catch (error) {
      console.error("Error fetching drivers:", error);
      handleApiError(error);
      return [];
    }
  };

  /**
   * Fetches all sites for the organization
   * @returns {Array} Array of site objects
   */
  const getAllSites = async () => {
    try {
      const organizationId = userState?.user?.organizationId;
      const resp = await axios.get(
        `${config.baseURL}/site/organization?organizationId=${organizationId}`,
        {
          withCredentials: true,
        }
      );
      return resp.data;
    } catch (error) {
      handleApiError(error);
      return [];
    }
  };

  /**
   * Handles API errors consistently across all API calls
   * @param {Error} error - The error object from the API call
   */
  const handleApiError = (error) => {
    console.error("API Error:", error);
    if (error.response?.status === 401) {
      navigate("/");
    }
  };

  /**
   * Processes raw data and updates the overview state
   * @param {Object} rawData - Object containing all fetched data
   * @param {Object} alertsData - Object containing all fetched alerts
   */
  // const processOverviewData = (rawData, alertsData) => {
  //   const { vehicles, routes, stops, drivers, sites } = rawData;
  //   const { userAlerts, vehicleAlerts, expiryAlerts } = alertsData;
  //   const totalAlerts =
  //     userAlerts.length + vehicleAlerts.length + expiryAlerts.length;
  //   setOverview({
  //     vehicles: vehicles.length,
  //     routes: routes.length,
  //     stops: stops.length,
  //     drivers: drivers.length,
  //     sites: sites.length,
  //     activeVehicles: vehicles.filter((v) => v.status === "ACTIVE").length,
  //     inactiveVehicles: vehicles.filter(
  //       (v) => v.status === "INACTIVE" || v.status === "DEAD" || !v.status
  //     ).length,
  //     deadVehicles: vehicles.filter((v) => v.status === "DEAD" || !v.status)
  //       .length,
  //     totalAlerts: totalAlerts,
  //     vehicleAlerts: vehicleAlerts.length,
  //     userAlerts: userAlerts.length,
  //     expiryAlerts: expiryAlerts.length,
  //   });
  // };

  // Data Fetching Effect
  const processOverviewData = (rawData, alertsData) => {
    const { vehicles, routes, stops, drivers, sites } = rawData;
    const { userAlerts, vehicleAlerts, expiryAlerts, totalAlerts } = alertsData;

    setOverview({
      vehicles: vehicles.length,
      routes: routes.length,
      stops: stops.length,
      drivers: drivers.length,
      sites: sites.length,
      activeVehicles: vehicles.filter((v) => v.status === "ACTIVE").length,
      inactiveVehicles: vehicles.filter(
        (v) => v.status === "INACTIVE" || v.status === "DEAD" || !v.status
      ).length,
      totalAlerts: totalAlerts || 0,
      vehicleAlerts: vehicleAlerts.length,
      userAlerts: userAlerts.length,
      expiryAlerts: expiryAlerts.length,
    });
  };

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      setIsLoading(true);
      try {
        const organizationId = userState?.user?.organizationId;
        const fetchPromises = [
          getAllVehicles(),
          getAllRoutes(),
          getAllStops(),
          getAllAlerts(),
          getAllDrivers(),
          getAllSites(),
        ];

        const [vehicles, routes, stops, alertsData, drivers, sites] =
          await Promise.all(fetchPromises);

        // Add settings fetch if organizationId exists
        // if (organizationId) {
        //     fetchPromises.push(fetchSettings(organizationId));
        // } else {
        //     console.warn("No organizationId found, skipping settings fetch");
        // }

        // Fetch settings separately
        if (organizationId) {
          await fetchSettings(organizationId);
        } else {
          console.warn("No organizationId found, skipping settings fetch");
        }

        if (isMounted) {
          const newData = { vehicles, routes, stops, drivers, sites };
          setData(newData);
          setAlerts(alertsData);
          processOverviewData(newData, alertsData);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [userState?.user?.organizationId, fetchSettings]); // Added dependencies

  // Effect to store original card keys when overview data changes
  useEffect(() => {
    if (Object.keys(overview).length > 0) {
      storeOriginalCardKeys();
    }
  }, [overview]);

  // Generate and filter card data with memoization
  const filteredCards = useMemo(() => {
    const cardData = generateCardData(overview, t);
    return filterCardsByPermission(cardData);
  }, [overview, t, currentLanguage, userState?.features, cardKeyMap]);

  // Render dashboard card skeletons when loading
  const renderCardSkeletons = () => {
    return Array(6)
      .fill(0)
      .map((_, index) => (
        <div key={`skeleton-card-${index}`} className="dashboard-card">
          <Skeleton
            variant="rectangular"
            width="100%"
            height={40}
            sx={{ mb: 1 }}
          />
          <Skeleton variant="text" width="70%" height={30} />
          <Skeleton
            variant="rectangular"
            width="100%"
            height={120}
            sx={{ mt: 2 }}
          />
        </div>
      ));
  };

  // Render upcoming trip skeleton when loading
  const renderUpcomingTripSkeleton = () => (
    <div className="dashboard-upcoming-section">
      <Skeleton variant="rectangular" width="100%" height={50} sx={{ mb: 2 }} />
      <Skeleton variant="rectangular" width="100%" height={200} />
    </div>
  );

  // Render stylish empty state when no permissions
  const renderNoPermissionsState = () => (
    <div className="no-permissions-state">
      <p>{t("")}</p>
    </div>
  );

  // Render
  return (
    <div className="dashboard-container">
      <span className="dashboard-title">{t(DASHBOARD)}</span>
      <div className="row">
        {isLoading || settingsLoading ? (
          <>
            <div className="dashboard-grid-container">
              {renderCardSkeletons()}
            </div>
            {renderUpcomingTripSkeleton()}
          </>
        ) : !settings ? (
          <div className="error-state">
            <p>{t("failed_to_load_settings")}</p>
          </div>
        ) : (
          <>
            {filteredCards.length > 0 ? (
              <>
                <div className="dashboard-grid-container">
                  {filteredCards.map((card) => (
                    <DashboardCard
                      key={card.id}
                      {...card}
                      mapView={settings?.defaultMapView} // Pass settings
                      mapLayers={settings?.defaultMapLayers}
                    />
                  ))}
                </div>
                <div className="dashboard-upcoming-section">
                  <DashboardUpcomingTrip />
                </div>
              </>
            ) : (
              renderNoPermissionsState()
            )}
          </>
        )}
      </div>
    </div>
  );
}
